import { StyledTooltip } from "@/components/StyledTooltip/StyledTooltip";
import { MAX_BADGE_SIZE } from "@/constants/badges";
import { usePageTabs, useTablePageTabs } from "@/hooks/tab";
import { RootState } from "@/store/store";
import { TabList as MuiTabList } from "@mui/lab";
import { TabListProps } from "@mui/lab/TabList";
import {
  Badge,
  Box,
  Divider,
  Tab as MuiTab,
  Tabs as MuiTabs,
  Skeleton,
  TabProps,
  TabsProps,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface TabsHeaderProps {
  title?: React.ReactNode;
  defaultTabValue: string;
  buttons?: React.ReactNode;
  tabs: {
    label: React.ReactNode;
    tooltip?: React.ReactNode;
    icon?: React.ReactElement;
    value: string;
    flashing?: boolean;
    badgeContent?: (state: RootState) => number;
    whileSelected?: () => void;
  }[];
}

const outerTabs = (inner: () => React.ReactNode, buttons: () => React.ReactNode) => (
  <>
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        alignContent: "center",
        width: "100%"
      }}
    >
      {inner()}
      <Box
        component="div"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "6px"
        }}
      >
        {buttons()}
      </Box>
    </Box>
    <Divider sx={{ marginBottom: "1em" }} flexItem />
  </>
);

export const Tab = ({
  badgeContent,
  whileSelected,
  selectedTab,
  flashing,
  tooltip,
  ...props
}: TabProps & {
  icon: React.ReactNode;
  selectedTab: string;
  badgeContent?: (state: RootState) => number;
  whileSelected?: () => void;
  flashing?: boolean;
  tooltip?: React.ReactNode;
}) => {
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));
  const badgeSelection = useSelector(badgeContent ?? (() => 0));

  useEffect(() => {
    if (whileSelected && selectedTab === props.value) {
      whileSelected();
    }
  });
  const FlashingDisplay = () => (
    <Skeleton
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: "0px",
        left: "0px",
        background: "transparent",
        "&::after": {
          background: (theme) => `linear-gradient( 90deg, transparent, ${alpha(theme.palette.error.light, 0.1)}, transparent )`
        }
      }}
      animation="wave"
    />
  );
  return (
    <StyledTooltip title={tooltip}>
      <MuiTab
        {...props}
        icon={
          <Badge color="error" variant="standard" badgeContent={badgeSelection} max={MAX_BADGE_SIZE}>
            {props.icon}
          </Badge>
        }
        sx={{
          textTransform: "unset",
          "& span": {
            marginRight: ["2px!important", "2px!important", "8px!important"]
          },
          padding: props.icon !== undefined ? { xs: "8px", lg: "12px" } : { xs: "10px", md: "12px" },
          position: "relative",
          ...props.sx,
          minHeight: "min-content",
          minWidth: {
            xs: "52px !important",
            sm: "70px !important",
            md: "90px !important"
          }
        }}
        {...(mdDown || props.label == null
          ? {
              iconPosition: undefined,
              label: (
                <>
                  {flashing && <FlashingDisplay />}
                  {props.icon === undefined && props.label}
                </>
              )
            }
          : {
              iconPosition: "start", // eslint-disable-line i18next/no-literal-string
              label: (
                <>
                  {flashing && <FlashingDisplay />}
                  {props.label}
                </>
              )
            })}
      />
    </StyledTooltip>
  );
};

export const Tabs = ({
  tab,
  setTab,
  defaultTab,
  children,
  buttons,
  ...props
}: {
  tab: string | false;
  setTab: (tab: string | false) => void;
  defaultTab: string;
  children: React.ReactNode;
  buttons?: React.ReactNode;
} & TabsProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!tab) {
      setTab(defaultTab);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return outerTabs(
    () => (
      <MuiTabs
        aria-label={t("label.tabs")}
        {...props}
        value={tab}
        sx={{
          width: "100%",
          maxHeight: "max-content",
          ...props.sx
        }}
        onChange={(event: React.SyntheticEvent, newValue: string) => setTab(newValue)}
      >
        {children}
      </MuiTabs>
    ),
    () => buttons
  );
};

export const TabList = ({ buttons, ...props }: TabListProps & { buttons?: React.ReactNode }) => {
  return outerTabs(
    () => <MuiTabList {...props} />,
    () => buttons
  );
};

export const TabsHeader = ({
  title,
  tab,
  changeTab,
  defaultTabValue,
  buttons,
  tabs
}: TabsHeaderProps & {
  tab: string;
  changeTab: (tab: string | false) => void;
}) => {
  return (
    <>
      {title && (
        <Typography variant="h1" sx={{ marginBottom: "0.5em" }}>
          {title}
        </Typography>
      )}
      <Tabs tab={tab} setTab={changeTab} defaultTab={defaultTabValue} buttons={buttons}>
        {tabs.map((tabData) => (
          <Tab
            key={tabData.value}
            selectedTab={tab}
            icon={tabData.icon}
            label={tabData.label}
            value={tabData.value}
            badgeContent={tabData.badgeContent}
            whileSelected={tabData.whileSelected}
            flashing={tabData.flashing}
            tooltip={tabData.tooltip}
          />
        ))}
      </Tabs>
    </>
  );
};

export const TabsPageHeader = ({
  tabPath,
  onEnterTab,
  defaultTabValue,
  ...props
}: TabsHeaderProps & {
  tabPath: string;
  onEnterTab?: (tab: string | false) => void;
}) => {
  const tabValues = props.tabs.map((tab) => tab.value);
  const { tab, changeTab } = usePageTabs(tabPath, tabValues, defaultTabValue);

  return (
    <TabsHeader
      tab={tab}
      changeTab={(tab) => {
        changeTab(tab);
        if (onEnterTab) {
          onEnterTab(tab);
        }
      }}
      defaultTabValue={defaultTabValue}
      {...props}
    />
  );
};

export const TabsTablePageHeader = ({
  tabPath,
  table,
  onEnterTab,
  defaultTabValue,
  ...props
}: TabsHeaderProps & {
  tabPath: string;
  table: string;
  onEnterTab?: (tab: string | false) => void;
}) => {
  const tabValues = props.tabs.map((tab) => tab.value);
  const { tab, changeTab } = useTablePageTabs(tabPath, table, tabValues, defaultTabValue);

  return (
    <TabsHeader
      tab={tab}
      changeTab={(tab) => {
        changeTab(tab);
        if (onEnterTab) {
          onEnterTab(tab);
        }
      }}
      defaultTabValue={defaultTabValue}
      {...props}
    />
  );
};
